import React, { useState } from 'react'
import { useHistory } from 'react-router-dom'
import { minBy } from 'lodash'
import Card from '@material-ui/core/Card'
import CardContent from '@material-ui/core/CardContent'
import Box from '@material-ui/core/Box'
import Chip from '@material-ui/core/Chip'
import Typography from '@material-ui/core/Typography'
import Tooltip from '@material-ui/core/Tooltip'
import LinkOutlinedIcon from 'components/material-symbol/icons/Link'
import EditOutlinedIcon from 'components/material-symbol/icons/Edit'
import LockOutlinedIcon from 'components/material-symbol/icons/Lock'
import LocationOnOutlinedIcon from 'components/material-symbol/icons/LocationOn'
import ScheduleOutlinedIcon from 'components/material-symbol/icons/Schedule'
import { makeStyles } from '@material-ui/core/styles'
import IconButton from '@material-ui/core/IconButton'
import CollectionsBookmarkOutlinedIcon from 'components/material-symbol/icons/CollectionsBookmark'
import VideocamOutlinedIcon from 'components/material-symbol/icons/Videocam'
import SocialShareDialog from 'components/social-share-dialog'
import pluralize from 'lib/pluralize'
import clsx from 'clsx'
import LessonPackDetailsDialog from 'components/lesson-pack-details-dialog'
import { toFullTextDayDateWithTimezone } from 'utils/dateUtils'
import { durationText } from 'utils/durationUtils'
import RedeemIcon from 'components/material-symbol/icons/Redeem'
import VisibilityOffOutlinedIcon from 'components/material-symbol/icons/VisibilityOff'
import MoreVertIcon from 'components/material-symbol/icons/MoreVert'
import { getPaymentAccountWarningChip } from '../events/utils'
import { centsToDollarsFormatted } from 'lib/utils/number'
import BookingTypeActionMenu from './booking-type-action-menu'
import BookingTypeDetailsDialog, {
  formatBookingTypeUrl,
} from './booking-type-details-dialog'
import { interactiveCardHoverStyles } from 'lib/styles/interactive-card-hover'
import {
  formatExpiresAt,
  formatTurnaround,
  isVirtualCoaching,
} from './offering-kind'

const useStyles = makeStyles(theme => ({
  card: {
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minHeight: 288,
    borderRadius: '12px',
    overflow: 'hidden',
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.common.white,
    '&.MuiPaper-rounded': {
      borderRadius: '12px',
    },
    '&.MuiPaper-root': {
      boxShadow: 'none',
    },
    ...interactiveCardHoverStyles(theme),
  },
  cardContent: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    padding: '0 !important',
    '&.MuiCardContent-root:last-child': {
      paddingBottom: '0 !important',
    },
  },
  cardInner: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: theme.spacing(3),
    paddingRight: theme.spacing(2),
    paddingBottom: theme.spacing(3),
    paddingLeft: theme.spacing(2),
    backgroundColor: theme.palette.common.white,
    cursor: 'pointer',
  },
  badgeRow: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: 6,
    marginBottom: theme.spacing(2),
  },
  iconBadge: {
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& svg, & .MuiSvgIcon-root': {
      width: 14,
      height: 14,
      fontSize: 14,
    },
  },
  chipLessonPack: {
    backgroundColor: '#D2DEE3',
    color: '#172024',
  },
  chipAccessCode: {
    backgroundColor: '#F1E9D8',
    color: '#5C4922',
  },
  chipVirtualCoaching: {
    backgroundColor: '#D2DEE3',
    color: '#172024',
  },
  chipPrivate: {
    backgroundColor: '#CCDDFF',
    color: '#001C57',
  },
  chip: {
    borderRadius: 9999,
    height: '22px',
    fontSize: '10px',
    border: 'none',
    fontWeight: 500,
  },
  chipRemaining: {
    backgroundColor: '#EBF0F2',
    color: '#172024',
  },
  chipGiftable: {
    backgroundColor: '#CCDDFF',
    color: '#001C57',
  },
  cardTitle: {
    marginBottom: theme.spacing(2),
    display: '-webkit-box',
    lineClamp: 1,
    WebkitBoxOrient: 'vertical',
    overflow: 'clip',
    textOverflow: 'ellipsis',
    lineHeight: 1.3,
    fontSize: '1rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  metadataRow: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'flex-start',
    gap: theme.spacing(1),
    marginBottom: theme.spacing(1.5),
  },
  metadataItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 6,
    minWidth: 0,
    maxWidth: '100%',
  },
  metadataIcon: {
    color: theme.palette.text.secondary,
    width: 14,
    height: 14,
    fontSize: 14,
    flexShrink: 0,
    opacity: 0.85,
  },
  metadataText: {
    color: theme.palette.text.secondary,
    fontSize: '0.8125rem',
    lineHeight: 1.3,
  },
  price: {
    fontSize: '1.125rem',
    fontWeight: 700,
    color: theme.palette.text.primary,
    lineHeight: 1,
    margin: 0,
  },
  cardFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginTop: 'auto',
    paddingTop: theme.spacing(2),
  },
  actionButtons: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(1),
    flexShrink: 0,
    marginLeft: theme.spacing(1),
  },
  outlinedIconButton: {
    minWidth: 40,
    width: 40,
    height: 40,
    padding: theme.spacing(1),
    border: '1px solid #C4C7C9',
    borderRadius: '50%',
    color: '#172024',
    backgroundColor: theme.palette.common.white,
    '&:hover': {
      backgroundColor: '#F5F7F8',
      borderColor: '#C4C7C9',
    },
    '& .MuiSvgIcon-root': {
      width: 20,
      height: 20,
      fontSize: 20,
    },
  },
  errorIcon: {
    color: '#000000DE',
  },
  unableToAcceptPaymentsChip: {
    background: '#F88078',
    fontSize: '10px',
    height: '22px',
  },
  payoutsOnHoldChip: {
    background: '#FFB547',
    fontSize: '10px',
    height: '22px',
  },
}))

const IconBadge = ({ label, className, children }) => (
  <Tooltip title={label}>
    <Box
      component="span"
      className={clsx(className)}
      aria-label={label}
      role="img"
    >
      {children}
    </Box>
  </Tooltip>
)

const getDisplayPrice = bookingType => {
  if (bookingType.lessonPacks?.length > 0) {
    const lowestPricePack = minBy(bookingType.lessonPacks, 'priceInCents')
    if (lowestPricePack.priceInCents === 0) {
      return 'Free'
    }
    return centsToDollarsFormatted(lowestPricePack.priceInCents)
  }

  if (bookingType.priceInCents) {
    return centsToDollarsFormatted(bookingType.priceInCents)
  }

  return 'Free'
}

const getPackQuantityLabel = lessonPacks => {
  if (!lessonPacks?.length) return null

  const quantities = lessonPacks.map(lessonPack => lessonPack.quantity)
  const packWord = pluralize(quantities.length, 'pack')

  if (quantities.length === 1) {
    return `${quantities[0]} pack`
  }

  if (quantities.length === 2) {
    return `${quantities[0]} and ${quantities[1]} ${packWord}`
  }

  const last = quantities[quantities.length - 1]
  const rest = quantities.slice(0, -1).join(', ')

  return `${rest}, and ${last} ${packWord}`
}

const composeBadges = (bt, isLessonPack, classes) => {
  const paymentAccountWarningChip = getPaymentAccountWarningChip({
    event: bt,
    classes,
    isMobile: true,
  })

  return (
    <>
      {bt.accessCode ? (
        <IconBadge
          label="Access Code"
          className={clsx(classes.iconBadge, classes.chipAccessCode)}
        >
          <LockOutlinedIcon />
        </IconBadge>
      ) : null}
      {bt.status === 'INACTIVE' ? (
        <IconBadge
          label="Private"
          className={clsx(classes.iconBadge, classes.chipPrivate)}
        >
          <VisibilityOffOutlinedIcon />
        </IconBadge>
      ) : null}
      {isVirtualCoaching(bt) ? (
        <IconBadge
          label="Virtual Coaching"
          className={clsx(classes.iconBadge, classes.chipVirtualCoaching)}
        >
          <VideocamOutlinedIcon />
        </IconBadge>
      ) : null}
      {bt.lessonPacks.length > 0 ? (
        <IconBadge
          label="Lesson Pack"
          className={clsx(classes.iconBadge, classes.chipLessonPack)}
        >
          <CollectionsBookmarkOutlinedIcon />
        </IconBadge>
      ) : null}
      {bt.giftingEnabled ? (
        <IconBadge
          label="Giftable"
          className={clsx(classes.iconBadge, classes.chipGiftable)}
        >
          <RedeemIcon />
        </IconBadge>
      ) : null}
      {isLessonPack && bt.remainingCredits > 0 ? (
        <Chip
          label={`${bt.remainingCredits}/${bt.totalCredits} Remaining`}
          className={clsx(classes.chip, classes.chipRemaining)}
        />
      ) : null}
      {paymentAccountWarningChip}
    </>
  )
}

const BookingTypeCard = ({ bookingType, isLessonPack = false, refetch }) => {
  const classes = useStyles()
  const history = useHistory()
  const [isOpen, setIsOpen] = useState(false)
  const [isDetailsOpen, setIsDetailsOpen] = useState(false)
  const [actionMenuAnchor, setActionMenuAnchor] = useState(null)
  const bookingTypeUrl = formatBookingTypeUrl(bookingType)
  const packQuantityLabel = getPackQuantityLabel(bookingType.lessonPacks)
  const displayPrice = getDisplayPrice(bookingType)
  const isExpired =
    bookingType.expiresAt && Date.parse(bookingType.expiresAt) < Date.now()

  const handleCardClick = () => {
    if (isLessonPack) {
      if (!isExpired) {
        setIsOpen(true)
      }
      return
    }
    setIsDetailsOpen(true)
  }

  const handleActionClick = event => {
    event.stopPropagation()
  }

  // Virtual coaching offerings have no location.
  const locationName = bookingType.locations?.[0]?.name

  return (
    <Card elevation={0} className={classes.card}>
      <CardContent className={classes.cardContent}>
        <Box className={classes.cardInner} onClick={handleCardClick}>
          <Box className={classes.badgeRow}>
            {composeBadges(bookingType, isLessonPack, classes)}
          </Box>

          <Typography
            component="h3"
            variant="subtitle1"
            className={classes.cardTitle}
          >
            {bookingType.title}
          </Typography>

          <Box className={classes.metadataRow}>
            {locationName ? (
              <Box className={classes.metadataItem}>
                <LocationOnOutlinedIcon className={classes.metadataIcon} />
                <Typography
                  variant="caption"
                  className={classes.metadataText}
                  noWrap
                  title={locationName}
                >
                  {locationName}
                </Typography>
              </Box>
            ) : null}
            <Box className={classes.metadataItem}>
              <ScheduleOutlinedIcon className={classes.metadataIcon} />
              <Typography variant="caption" className={classes.metadataText}>
                {isVirtualCoaching(bookingType)
                  ? `${formatTurnaround(
                    bookingType.virtualCoaching?.turnaroundHours,
                  )} turnaround`
                  : durationText(bookingType.durationInMinutes)}
              </Typography>
            </Box>
            {packQuantityLabel ? (
              <Box className={classes.metadataItem}>
                <CollectionsBookmarkOutlinedIcon
                  className={classes.metadataIcon}
                />
                <Typography variant="caption" className={classes.metadataText}>
                  {packQuantityLabel}
                </Typography>
              </Box>
            ) : null}
          </Box>

          {isLessonPack ? (
            <Typography
              variant="caption"
              className={classes.metadataText}
              display="block"
            >
              {bookingType.expiresAt
                ? `Expires ${toFullTextDayDateWithTimezone(
                  bookingType.expiresAt,
                )}`
                : 'Does not expire'}
            </Typography>
          ) : null}

          {isVirtualCoaching(bookingType) ? (
            <Typography
              variant="caption"
              className={classes.metadataText}
              display="block"
            >
              {formatExpiresAt(bookingType.virtualCoaching?.expiresAt)}
            </Typography>
          ) : null}

          <Box className={classes.cardFooter}>
            {displayPrice ? (
              <Typography component="p" className={classes.price}>
                {displayPrice}
              </Typography>
            ) : (
              <span />
            )}
            <Box className={classes.actionButtons} onClick={handleActionClick}>
              <Tooltip
                title={
                  isExpired
                    ? 'Lesson Pack has expired and cannot be edited'
                    : ''
                }
              >
                <IconButton
                  data-cy={bookingType.title + ' edit'}
                  aria-label="Edit"
                  className={classes.outlinedIconButton}
                  onClick={() => {
                    if (isExpired) return
                    return isLessonPack
                      ? setIsOpen(!isOpen)
                      : history.push(
                        `/pga-coach/bookings/booking-types/edit/${bookingType.id}`,
                      )
                  }}
                >
                  <EditOutlinedIcon
                    color={isExpired ? 'disabled' : 'inherit'}
                  />
                </IconButton>
              </Tooltip>
              {isOpen ? (
                <LessonPackDetailsDialog
                  isOpen={isOpen}
                  setIsOpen={setIsOpen}
                  booking={bookingType}
                  refetch={refetch}
                />
              ) : null}
              {bookingTypeUrl && (
                <SocialShareDialog
                  trigger={
                    <IconButton
                      aria-label="Share booking link"
                      className={classes.outlinedIconButton}
                    >
                      <LinkOutlinedIcon />
                    </IconButton>
                  }
                  shareTitle={bookingType.title}
                  shareUrl={bookingTypeUrl}
                  dialogTitle="Share booking link"
                  dialogSubtitle="Share this booking type with your students."
                  showBookingUrl
                  bookingType={bookingType}
                  att="pga-lesson-type"
                />
              )}
              {!isLessonPack ? (
                <>
                  <IconButton
                    aria-label="More actions"
                    className={classes.outlinedIconButton}
                    onClick={event => {
                      event.stopPropagation()
                      setActionMenuAnchor(event.currentTarget)
                    }}
                  >
                    <MoreVertIcon />
                  </IconButton>
                  <BookingTypeActionMenu
                    bookingType={bookingType}
                    isOpen={Boolean(actionMenuAnchor)}
                    anchorEl={actionMenuAnchor}
                    handleClose={() => setActionMenuAnchor(null)}
                    onDeleted={refetch}
                  />
                </>
              ) : null}
            </Box>
          </Box>
        </Box>
      </CardContent>
      {isDetailsOpen ? (
        <BookingTypeDetailsDialog
          isOpen={isDetailsOpen}
          setIsOpen={setIsDetailsOpen}
          bookingType={bookingType}
          isLessonPack={isLessonPack}
        />
      ) : null}
    </Card>
  )
}

export default BookingTypeCard
